import Image from "next/image";
import type { ChakraProps, TextProps } from "@chakra-ui/react";
import { Badge, Text } from "@chakra-ui/react";
import React from "react";
import type { BadgeType } from "@shared/graphql.types";
import { DiamondIcon, PieIcon } from "components/SvgIcons";
import { colours } from "@shared/ui.design-system";

type Props = ChakraProps & {
  badge: BadgeType;
  gallery?: boolean;
  textProps?: TextProps;
};

const unoptimized = process.env.STORYBOOK === "true";

export const ProductCardBadgeLabel: React.FC<Props> = ({
  badge,
  gallery = false,
  textProps = {},
  ...chakraProps
}) => {
  const { text, badge: badgeLabel } = badge;
  if (!badgeLabel?.length) return null;

  const textComp = (variant) => (
    <Text
      variant={variant}
      {...chakraProps}
      {...textProps}
      top={gallery ? 2 : 0}
    >
      {text}
    </Text>
  );

  const awardBadgeComp = (variant) => {
    const { media } = badge;
    if (!media) return null;
    const size = [
      gallery ? 100 : 55,
      gallery ? 150 : 70,
      gallery ? 200 : "40%"
    ];
    const left = [1, gallery ? 5 : -1];
    const top = [1, gallery ? 5 : 0];

    return (
      <Badge
        variant={variant}
        {...chakraProps}
        w={size}
        h={size}
        top={top}
        left={left}
        as="div"
      >
        {/* Reason we're using it as a div is because SwiperSlide has fixed size for span */}
        <Image
          src={media?.thumbnails?.size_webimage as string}
          unoptimized={unoptimized}
          fill
          sizes="(max-width: 768px) 3.4rem, (max-width: 1200px) 6.25rem"
          style={{
            objectFit: "contain"
          }}
          alt={media.description || ""}
        />
      </Badge>
    );
  };

  const mapBadgeToComponent: Record<string, null | JSX.Element> = {
    award: awardBadgeComp("upperRightCorner"),
    newIn: textComp("availabilityWhite"),
    comingSoon: textComp("availabilityWhite"),
    limitedEdition: textComp("availabilityPink"),
    moreStockComingSoon: textComp("availabilityWhite"),
    lastCall: textComp("availabilityPink"),
    backInStock: textComp("availabilityPink"),
    treasureHunt: (
      <Text variant="treasureHunt" {...chakraProps}>
        <DiamondIcon w="20px" h="20px" />
      </Text>
    ),
    pieDay: (
      <Text variant="pieDay" {...chakraProps} top={gallery ? 2 : 0}>
        PieDay <PieIcon color={colours.accent.strong} boxSize="1.5rem" />
      </Text>
    )
  };

  return mapBadgeToComponent[badgeLabel] !== undefined
    ? mapBadgeToComponent[badgeLabel]
    : textComp("availabilityWhite");
};
